import React, { useState, useEffect } from 'react';
import DatePicker from 'react-datepicker';
import { format } from 'date-fns';
import { Calendar } from 'lucide-react';
import "react-datepicker/dist/react-datepicker.css";

interface DateRangePickerProps {
  startDate: Date | null | undefined;
  endDate: Date | null | undefined;
  onChange: (start: Date | null | undefined, end: Date | null | undefined) => void;  // Accept undefined
}

const textStyles = {
  // Base text styles
  base: {
    fontFamily: 'var(--chat-font-family)',
    fontSize: 'var(--planner-body-size)',
    fontWeight: 'var(--planner-font-weight-normal)',
    lineHeight: 'var(--chat-line-height)',
    color: 'var(--planner-text-primary)'
  },
  // Header styles
  h2: {
    fontSize: 'var(--planner-heading-size)',
    fontWeight: 'var(--planner-font-weight-semibold)'
  },
  h3: {
    fontSize: 'var(--planner-subheading-size)',
    fontWeight: 'var(--planner-font-weight-medium)'
  },
  h4: {
    fontSize: 'var(--planner-body-size)',
    fontWeight: 'var(--planner-font-weight-medium)'
  },
  // Text sizes
  large: {
    fontSize: 'var(--planner-subheading-size)'
  },
  small: {
    fontSize: 'var(--planner-small-size)'
  },
  xs: {
    fontSize: 'calc(var(--chat-font-size) * 0.833)' // 10px if base is 12px
  },
  // Font weights
  medium: {
    fontWeight: 'var(--planner-font-weight-medium)'
  },
  semibold: {
    fontWeight: 'var(--planner-font-weight-semibold)'
  },
  // Colors
  secondary: {
    color: 'var(--planner-text-secondary)'
  },
  tertiary: {
    color: 'var(--planner-text-tertiary)'
  }
};

export function DateRangePicker({ startDate, endDate, onChange }: DateRangePickerProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [focusedInput, setFocusedInput] = useState<'start' | 'end' | null>(null);

  // Handle input click
  const handleInputClick = (input: 'start' | 'end') => {
    setFocusedInput(input);
    setIsOpen(true);
  };

  const getMaxDurationDays = () => {
    const cssValue = getComputedStyle(document.documentElement)
      .getPropertyValue('--date-picker-max-duration-days');
    return parseInt(cssValue) || 21; // Default to 21 if not set
  };

  // Close the calendar when clicking outside
  useEffect(() => {
    if (!isOpen) return;
    
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      const datePickerElements = document.querySelectorAll('.react-datepicker, .react-datepicker-wrapper, .date-input');
      
      // Check if click was inside any of the datepicker elements
      let isInsideDatePicker = false;
      datePickerElements.forEach(element => {
        if (element.contains(target)) {
          isInsideDatePicker = true;
        }
      });
      
      if (!isInsideDatePicker) {
        setIsOpen(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  // Custom styles to be injected for calendar layout
  useEffect(() => {
    // Add custom styles to make months appear side by side and center within the panel
    const styleEl = document.createElement('style');
    styleEl.innerHTML = `
      /* Container */
      .date-range-picker-container {
        position: relative;
      }
      
      .date-range-picker-container .grid {
        gap: var(--date-picker-input-gap) !important;
      }
      
      .calendar-popup-container {
        margin-top: var(--date-picker-calendar-gap) !important;
      }
      
      /* Wrapper */
      .react-datepicker-wrapper {
        width: 100% !important;
      }
      
      /* Main Calendar */
      .react-datepicker {
        width: var(--date-picker-width) !important;
        max-width: 100% !important;
        height: var(--date-picker-height) !important;
        display: flex !important;
        justify-content: center !important;
        margin: 0 auto !important;
        padding: var(--date-picker-inner-padding) !important;
        background-color: white !important;
        border: var(--date-picker-border-width) solid var(--date-picker-border-color) !important;
        font-family: var(--chat-font-family) !important;
        font-size: var(--date-picker-input-font-size) !important;
      }
      
      /* Month Container */
      .react-datepicker__month-container {
        float: none !important;
        width: var(--date-picker-month-container-width) !important;
        height: var(--date-picker-height) !important;
        display: flex !important;
        flex-direction: column !important;
      }
      
      /* Header */
      .react-datepicker__header {
        background-color: #f8f9fa !important;
        border-bottom: 1px solid #e9ecef !important;
        height: var(--date-picker-header-height) !important;
        padding-bottom: var(--date-picker-weekday-row-gap) !important;
      }
      
      .react-datepicker__current-month {
        font-weight: var(--planner-font-weight-medium) !important;
        font-size: var(--date-picker-month-font-size) !important;
        font-family: var(--chat-font-family) !important;
        color: var(--planner-text-primary) !important;
      }
      
      .react-datepicker__navigation {
        top: var(--date-picker-navigation-top) !important;
      }
      
      /* Days Grid */
      .react-datepicker__month {
        margin: var(--date-picker-month-margin) !important;
        height: var(--date-picker-month-height) !important;
        display: flex !important;
        flex-direction: column !important;
        justify-content: space-between !important;
      }
      
      /* Day Names */
      .react-datepicker__day-name {
        margin: var(--date-picker-day-margin) !important;
        width: var(--date-picker-day-size) !important;
        color: var(--planner-text-secondary) !important;
        font-family: var(--chat-font-family) !important;
        font-size: var(--date-picker-day-name-font-size) !important;
        font-weight: var(--planner-font-weight-medium) !important;
      }
      
      /* Day Cells */
      .react-datepicker__day {
        margin: var(--date-picker-day-margin) !important;
        width: var(--date-picker-day-size) !important;
        line-height: var(--date-picker-day-size) !important;
        border-radius: 0.25rem !important;
        color: var(--planner-text-primary) !important;
        font-family: var(--chat-font-family) !important;
        font-size: var(--date-picker-day-font-size) !important;
      }
      
      /* Day States */
      .react-datepicker__day:hover {
        background-color: var(--date-picker-hover-bg) !important;
        color: var(--date-picker-hover-text) !important;
      }
      
      .react-datepicker__day--today {
        font-weight: var(--planner-font-weight-medium) !important;
        color: var(--date-picker-today-text) !important;
      }
      
      .react-datepicker__day--selected,
      .react-datepicker__day--in-selecting-range,

      .react-datepicker__day--in-range,
      .react-datepicker__day--in-selecting-range {
        background-color: var(--date-picker-range-bg) !important;
        color: var(--date-picker-range-text) !important;
      }
      
      /* Ensure start/end dates are styled */
      .react-datepicker__day--selected.react-datepicker__day--range-start,
      .react-datepicker__day--selected.react-datepicker__day--range-end {
        background-color: var(--date-picker-selected-bg) !important;
        color: var(--date-picker-selected-text) !important;
      }
      
      .react-datepicker__day--selected.react-datepicker__day--in-range:not(.react-datepicker__day--range-start):not(.react-datepicker__day--range-end) {
        background-color: var(--date-picker-range-bg) !important;
        color: var(--date-picker-range-text) !important;
      }
      
      .react-datepicker__day--keyboard-selected {
        background-color: var(--date-picker-keyboard-selected-bg) !important;
        color: var(--date-picker-keyboard-selected-text) !important;
      }
      
      /* Disabled States */
      .react-datepicker__day--disabled {
        color: var(--planner-text-tertiary) !important;
        opacity: 0.4 !important;
        cursor: not-allowed !important;
        background-color: transparent !important;
      }
      
      .react-datepicker__day--disabled:hover {
        background-color: transparent !important;
        color: var(--planner-text-tertiary) !important;
        opacity: 0.4 !important;
      }
      
      .react-datepicker__day--outside-month {
        color: var(--planner-text-tertiary) !important;
        opacity: 0.3 !important;
      }
      
      /* Popper */
      .react-datepicker-popper {
        position: absolute !important;
        left: 0 !important;
        right: 0 !important;
        transform: none !important;
        margin-left: auto !important;
        margin-right: auto !important;
        width: var(--date-picker-width) !important;
        max-width: 100% !important;
      }
      
      /* Input Fields */
      .date-input {
        height: var(--date-picker-input-height) !important;
        padding: var(--date-picker-input-padding-y) var(--date-picker-input-padding-x) !important;
        font-size: var(--date-picker-input-font-size) !important;
      }
      
      /* Labels */
      .date-range-picker-container label {
        font-size: var(--date-picker-label-font-size) !important;
      }
      
      /* Vertical Spacing */
      .date-range-picker-container .space-y-4 > * + * {
        margin-top: var(--date-picker-vertical-spacing) !important;
      }
    `;
    document.head.appendChild(styleEl);
    
    return () => {
      document.head.removeChild(styleEl);
    };
  }, []);

  return (
    <div className="space-y-4 date-range-picker-container">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label 
            className="block"
            style={{
              ...textStyles.base,
              ...textStyles.regular,
              ...textStyles.regular,
              marginBottom: '0.5rem'
            }}
          >
            Start Date
          </label>
          <div className="relative">
           <input
            type="text"
            value={(() => {
              console.log('startDate type:', typeof startDate, 'value:', startDate);
              if (!startDate) return '';
              if (Array.isArray(startDate)) {
                console.error('startDate is an array!', startDate);
                return '';
              }
              return format(startDate, 'MMM d, yyyy');
            })()}
            onClick={() => handleInputClick('start')}
            readOnly
            placeholder="Start date"
              className={`w-full px-4 py-2 pl-8 border rounded-full shadow-sm cursor-pointer transition-all date-input ${
                focusedInput === 'start'
                  ? 'border-blue-500 ring-2 ring-blue-200'
                  : startDate
                  ? 'border-gray-300'
                  : 'border-gray-300 hover:border-gray-400'
              }`}
              style={{
                ...textStyles.base,
                ...textStyles.medium
              }}
            />
            <Calendar 
              size={18}
              className={`absolute left-3 top-1/2 transform -translate-y-1/2 transition-colors ${
                focusedInput === 'start' ? 'text-blue-500' : 'text-gray-400'
              }`}
              style={{ 
                width: 'var(--planner-icon-size-sm)', 
                height: 'var(--planner-icon-size-sm)' 
              }}
            />
          </div>
        </div>

        <div>
          <label 
            className="block"
            style={{
              ...textStyles.base,
              ...textStyles.regular,
              ...textStyles.regular,
              marginBottom: '0.5rem'
            }}
          >
            End Date
          </label>
          <div className="relative">
            <input
              type="text"
              value={endDate && !isNaN(endDate.getTime()) ? format(endDate, 'MMM d, yyyy') : ''}
              onClick={() => handleInputClick('end')}
              readOnly
              placeholder="End date"
              className={`w-full px-4 py-2 pl-8 border rounded-full shadow-sm cursor-pointer transition-all date-input ${
                focusedInput === 'end'
                  ? 'border-blue-500 ring-2 ring-blue-200'
                  : endDate
                  ? 'border-gray-300'
                  : 'border-gray-300 hover:border-gray-400'
              }`}
              style={{
                ...textStyles.base,
                ...textStyles.medium
              }}
            />
            <Calendar 
              size={18}
              className={`absolute left-3 top-1/2 transform -translate-y-1/2 transition-colors ${
                focusedInput === 'end' ? 'text-blue-500' : 'text-gray-400'
              }`}
              style={{ 
                width: 'var(--planner-icon-size-sm)', 
                height: 'var(--planner-icon-size-sm)' 
              }}
            />
          </div>
        </div>
      </div>

      {/* DatePicker for date selection - centered within parent container */}
      {isOpen && (
        <div className="calendar-popup-container" style={{ marginTop: 'var(--date-picker-calendar-gap, 1rem)' }}>
          <DatePicker
            selected={focusedInput === 'start' ? startDate : endDate}
            onChange={(date) => {
              if (focusedInput === 'start') {
                // If new start date is after current end date, clear the end date
                if (endDate && date && date > endDate) {
                  onChange(date, null);
                } else {
                  onChange(date, endDate);
                }
                setFocusedInput('end');
              } else {
                onChange(startDate, date);
                if (startDate && date) {
                  setIsOpen(false);
                  setFocusedInput(null);
                }
              }
            }}
            selectsStart={focusedInput === 'start'}
            selectsEnd={focusedInput === 'end'}
            startDate={startDate}
            endDate={endDate}
            minDate={focusedInput === 'start' ? new Date() : focusedInput === 'end' ? startDate : new Date()}
            maxDate={
              focusedInput === 'end' && startDate 
                ? new Date(startDate.getTime() + (getMaxDurationDays() * 24 * 60 * 60 * 1000))
                : undefined // No max date for start date selection
            }
            open={true}
            monthsShown={2}
            inline
            calendarClassName="shadow-lg border border-gray-200 rounded-lg bg-white"
          />
        </div>
      )}

      {/* Remove the error-causing date range display */}
      {/* Just show the 3-week instruction */}
      {(focusedInput === 'end' || (startDate && !endDate)) && (
        <div 
          style={{
            ...textStyles.base,
            ...textStyles.small,
            ...textStyles.secondary,
            marginTop: '0.5rem'
          }}
        >
          Maximum trip duration: 3 weeks
        </div>
      )}
      {/* {startDate && endDate && (
        <div 
          style={{
            ...textStyles.base,
            ...textStyles.medium,
            ...textStyles.secondary
          }}
        >
          {format(startDate, 'MMM d')} - {format(endDate, 'MMM d, yyyy')} 
          ({Math.ceil((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24))} days)
        </div>
      )} */}
    </div>
  );
}